import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminScreen, LoadOrError, adminErrorText, subscriptionTone, useResource } from './Overview';
import {
  Alert, Button, Card, EmptyState, Icon, Input, Modal, Select, Spinner, StatusPill, Textarea, useToast
} from '../../components/ui';
import { api } from '../../lib/api';
import { subscriptionLabel, brl, cycleLabel, datePt, dateTimePt } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

const STATUS_OPTIONS = ['trialing', 'active', 'past_due', 'expired', 'canceled', 'suspended'] as const;
type SubscriptionStatus = (typeof STATUS_OPTIONS)[number];

interface BusinessRow {
  id: string;
  name: string;
  slug: string;
  ownerEmail: string;
  status: string;
  subscriptionStatus: string | null;
  planName: string | null;
}

interface BusinessesPayload {
  businesses: BusinessRow[];
}

interface SubscriptionView {
  id: string;
  planKey: string;
  planName: string;
  billingCycle: string;
  status: SubscriptionStatus;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  provider: string;
  maxProfessionals: number;
}

interface EventRow {
  event: string;
  data: string | null;
  created_at: string;
}

interface TransactionRow {
  id: string;
  subscription_id: string;
  business_id: string;
  provider: string;
  provider_payment_id: string | null;
  kind: 'trial' | 'charge' | 'refund';
  amount_cents: number;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  paid_at: string | null;
  created_at: string;
}

interface DetailPayload {
  subscription: SubscriptionView;
  events: EventRow[];
  transactions: TransactionRow[];
}

const TX_LABEL: Record<string, string> = {
  pending: 'Aguardando pagamento',
  paid: 'Paga',
  failed: 'Falhou',
  refunded: 'Reembolsada'
};

const KIND_LABEL: Record<string, string> = {
  trial: 'Teste gratuito',
  charge: 'Cobrança',
  refund: 'Reembolso'
};

function transactionTone(status: string): 'neutral' | 'positive' | 'warning' | 'danger' {
  if (status === 'paid') return 'positive';
  if (status === 'pending') return 'warning';
  if (status === 'failed') return 'danger';
  return 'neutral';
}

function parseEvent(data: string | null): string {
  if (!data) return '';
  try {
    return JSON.stringify(JSON.parse(data));
  } catch {
    return String(data);
  }
}

export function AdminSubscriptionsPage(): JSX.Element {
  usePageMeta('Assinaturas — administração', 'Situação das assinaturas, eventos e transações por estabelecimento.', '/admin/assinaturas');

  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('empresa');
  const [filter, setFilter] = useState<string>('todas');

  const list = useResource<BusinessesPayload>(() => api.get<BusinessesPayload>('/api/admin/businesses'));
  const detail = useResource<DetailPayload | null>(
    () => (selectedId ? api.get<DetailPayload>(`/api/admin/subscriptions/${selectedId}`) : Promise.resolve(null)),
    [selectedId]
  );

  const rows = list.data?.businesses ?? [];
  const filtered = useMemo(
    () => (filter === 'todas' ? rows : rows.filter((r) => (r.subscriptionStatus ?? 'nenhuma') === filter)),
    [rows, filter]
  );

  const counters = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of rows) {
      const key = row.subscriptionStatus ?? 'nenhuma';
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [rows]);

  // -------- ações --------
  const [statusValue, setStatusValue] = useState<SubscriptionStatus | ''>('');
  const [pendingStatus, setPendingStatus] = useState<SubscriptionStatus | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelImmediate, setCancelImmediate] = useState(true);
  const [cancelReason, setCancelReason] = useState('');
  const [txPending, setTxPending] = useState<TransactionRow | null>(null);
  const [txNote, setTxNote] = useState('');
  const [txProviderId, setTxProviderId] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const sub = detail.data?.subscription ?? null;
  const selectedBusiness = rows.find((r) => r.id === selectedId) ?? null;
  const lifetimeLicense = Boolean(
    sub?.currentPeriodEnd && new Date(sub.currentPeriodEnd).getTime() > Date.now() + 2 * 365 * 24 * 60 * 60 * 1000
  );

  async function refreshAll(): Promise<void> {
    await Promise.all([list.reload(), detail.reload()]);
  }

  async function applyStatus(status: SubscriptionStatus): Promise<void> {
    if (!selectedId) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post<{ subscription: SubscriptionView }>(`/api/admin/subscriptions/${selectedId}/status`, { status });
      toast(tr('Assinatura marcada como «{status}». A mudança foi registrada em Auditoria.', { status: subscriptionLabel(status) }), 'success');
      setPendingStatus(null);
      await refreshAll();
    } catch (e) {
      setActionError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function applyCancel(): Promise<void> {
    if (!selectedId) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await api.post<{ subscription: SubscriptionView; warning?: string; providerCanceled?: boolean }>(
        `/api/admin/subscriptions/${selectedId}/cancel`,
        {
          immediate: cancelImmediate,
          reason: cancelReason.trim() || undefined
        }
      );
      if (res.warning) {
        setActionError(tr('{warning} Cancele a recorrência no painel do Asaas para interromper as cobranças.', { warning: res.warning }));
        toast('Assinatura cancelada na AgendaPro, mas a recorrência no Asaas não foi encerrada.', 'danger');
      } else {
        toast('Assinatura cancelada pelo administrador. Ação registrada em Auditoria.', 'success');
      }
      setCancelOpen(false);
      setCancelReason('');
      await refreshAll();
    } catch (e) {
      setActionError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function deleteAccount(): Promise<void> {
    if (!selectedId || !selectedBusiness) return;
    if (deleteConfirm.trim() !== selectedBusiness.name) {
      setActionError('Digite o nome do estabelecimento exatamente como aparece para confirmar a exclusão.');
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const res = await api.del<{ ok: boolean; deleted: { email: string; businessName: string } }>(
        `/api/admin/businesses/${selectedId}/account`
      );
      toast(tr('Conta {email} e estabelecimento «{business}» excluídos.', { email: res.deleted.email, business: res.deleted.businessName }), 'success');
      setDeleteOpen(false);
      setDeleteConfirm('');
      setParams({});
      await list.reload();
    } catch (e) {
      setActionError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function confirmManualTransaction(): Promise<void> {    if (!txPending) return;
    if (txNote.trim().length < 3) {
      setActionError('Descreva a justificativa da confirmação manual (mínimo de 3 caracteres).');
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const res = await api.post<{ ok: boolean; alreadyPaid?: boolean }>(
        `/api/admin/transactions/${txPending.id}/confirm-manual`,
        {
          providerPaymentId: txProviderId.trim() || undefined,
          note: txNote.trim()
        }
      );
      toast(
        res.alreadyPaid
          ? 'Esta transação já estava confirmada como paga; nada foi alterado.'
          : 'Pagamento confirmado manualmente e lançado em Auditoria.',
        'success'
      );
      setTxPending(null);
      setTxNote('');
      setTxProviderId('');
      await refreshAll();
    } catch (e) {
      setActionError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminScreen
      title={tr("Assinaturas e cobranças")}
      description={tr("O backend não expõe uma listagem global de assinaturas: esta tela usa a lista de estabelecimentos (que traz a situação da assinatura de cada um) e abre o detalhe completo por empresa.")}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="nl-label mb-0">{tr("Filtrar por situação")}</span>
        {['todas', ...Array.from(counters.keys())].map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`rounded-full border px-3 py-1 text-sm font-semibold ${
              filter === key ? 'border-transparent bg-accent text-on-accent shadow-[0_8px_16px_-10px_var(--nl-accent)]' : 'border-line bg-card text-muted hover:bg-surface'
            }`}
          >
            {key === 'todas' ? tr('Todas') : key === 'nenhuma' ? tr('Sem assinatura') : subscriptionLabel(key)}
            {key !== 'todas' ? ` (${counters.get(key) ?? 0})` : ` (${rows.length})`}
          </button>
        ))}
        <Button variant="ghost" disabled={list.loading} onClick={() => void list.reload()}>
          {list.loading ? <Spinner label="" /> : <Icon name="chart" />} {tr("Atualizar")}
        </Button>
      </div>

      <LoadOrError loading={list.loading} error={list.error} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="grid gap-2">
          {filtered.length === 0 && !list.loading && !list.error ? (
            <EmptyState title={tr("Nenhuma assinatura neste filtro")} description={tr("Selecione outra situação acima ou limpe o filtro.")} />
          ) : null}
          {filtered.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() => {
                setActionError(null);
                setStatusValue('');
                setParams({ empresa: row.id });
              }}
              className={`rounded-lg border p-4 text-left transition-colors ${
                selectedId === row.id ? 'border-accent bg-surface' : 'border-line bg-card hover:bg-surface'
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{row.name}</p>
                <StatusPill tone={subscriptionTone(row.subscriptionStatus)}>
                  {row.subscriptionStatus ? subscriptionLabel(row.subscriptionStatus) : 'Sem assinatura'}
                </StatusPill>
              </div>
              <p className="nl-hint mt-1">
                {row.planName ? tr('Plano {plan}', { plan: row.planName }) : tr('Plano não informado')} · {row.ownerEmail}
                {row.status === 'suspended' ? tr(' · estabelecimento suspenso') : ''}
              </p>
            </button>
          ))}
        </div>

        <div>
          {!selectedId ? (
            <Card>
              <h2 className="text-xl">{tr("Nenhuma empresa selecionada")}</h2>
              <p className="nl-hint mt-1">
                {tr("Escolha um estabelecimento à esquerda para ver assinatura, eventos e transações, mudar a situação, cancelar ou confirmar uma cobrança manualmente.")}
              </p>
            </Card>
          ) : null}

          {selectedId && detail.loading ? (
            <Card>
              <Spinner label={tr("Carregando assinatura e transações")} />
            </Card>
          ) : null}

          {selectedId && detail.error ? (
            <Alert tone="danger" title={tr("Não foi possível abrir a assinatura")}>
              {detail.error}
              {detail.error.includes('não encontrada')
                ? ' Esta empresa ainda não possui assinatura registrada no backend.'
                : null}
              <span className="mt-2 block">
                <Button variant="secondary" onClick={() => void detail.reload()}>{tr("Tentar novamente")}</Button>
              </span>
            </Alert>
          ) : null}

          {sub ? (
            <div className="grid gap-4">
              <Card>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl">{selectedBusiness?.name ?? 'Estabelecimento'}</h2>
                    <p className="nl-hint">{tr("Plano")} {sub.planName} ({sub.planKey}{tr(") · ciclo")} {cycleLabel(sub.billingCycle).toLowerCase()}</p>
                  </div>
                  <StatusPill tone={subscriptionTone(sub.status)}>{subscriptionLabel(sub.status)}</StatusPill>
                </div>

                <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div><dt className="nl-label mb-0 text-muted">{tr("Teste iniciado")}</dt><dd>{datePt(sub.trialStartedAt)}</dd></div>
                  <div><dt className="nl-label mb-0 text-muted">{tr("Teste termina")}</dt><dd>{datePt(sub.trialEndsAt)}</dd></div>
                  <div><dt className="nl-label mb-0 text-muted">{tr("Período atual")}</dt><dd>{datePt(sub.currentPeriodStart)} → {datePt(sub.currentPeriodEnd)}</dd></div>
                  <div><dt className="nl-label mb-0 text-muted">{tr("Cancela no fim do período")}</dt><dd>{sub.cancelAtPeriodEnd ? tr('Sim') : tr('Não')}</dd></div>
                  <div><dt className="nl-label mb-0 text-muted">{tr("Profissionais permitidos")}</dt><dd>{sub.maxProfessionals}</dd></div>
                  <div><dt className="nl-label mb-0 text-muted">{tr("Provedor de cobrança")}</dt><dd>{sub.provider || tr('nenhum informado')}</dd></div>
                  {sub.canceledAt ? (
                    <div className="sm:col-span-2"><dt className="nl-label mb-0 text-muted">{tr("Cancelada em")}</dt><dd>{dateTimePt(sub.canceledAt)}</dd></div>
                  ) : null}
                </dl>

                <div className="mt-5 grid gap-3 border-t border-line pt-4">
                  <div>
                    <label className="nl-label" htmlFor="sub-status-select">{tr("Mudar situação da assinatura")}</label>
                    <div className="flex flex-wrap items-end gap-2">
                      <Select
                        id="sub-status-select"
                        className="max-w-xs"
                        value={statusValue}
                        onChange={(e) => setStatusValue(e.target.value as SubscriptionStatus | '')}
                      >
                        <option value="">{tr("Escolha a nova situação…")}</option>
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option} value={option}>{subscriptionLabel(option)}</option>
                        ))}
                      </Select>
                      <Button
                        variant="secondary"
                        disabled={!statusValue || busy}
                        onClick={() => statusValue && setPendingStatus(statusValue)}
                      >
                        {tr("Aplicar situação")}
                      </Button>
                    </div>
                    <p className="nl-hint mt-1">{tr("A situação define se o estabelecimento pode operar a agenda. Toda mudança é auditada.")}</p>
                  </div>
                  <div>
                    <Button variant="danger" disabled={busy || sub.status === 'canceled'} onClick={() => { setActionError(null); setCancelOpen(true); }}>
                      {tr("Cancelar assinatura")}
                    </Button>
                    {sub.status === 'canceled' ? <p className="nl-hint mt-1">{tr("Esta assinatura já está cancelada.")}</p> : null}
                  </div>
                  {actionError ? <Alert tone="danger" title={tr("Ação não concluída")}>{actionError}</Alert> : null}
                </div>
              </Card>

              <Card>
                <h2 className="text-xl">{tr("Excluir conta")}</h2>
                <p className="nl-hint mt-1">
                  {tr("Remove definitivamente o login de")} <strong>{selectedBusiness?.ownerEmail ?? 'dono do estabelecimento'}</strong>{tr(", o estabelecimento «")}{selectedBusiness?.name ?? '—'}{tr("» e os dados operacionais: agenda, clientes, serviços, portfólio e transações.")}
                </p>
                <div className="mt-4">
                  <Button
                    variant="danger"
                    disabled={busy || sub.status !== 'canceled' || lifetimeLicense}
                    onClick={() => {
                      setActionError(null);
                      setDeleteConfirm('');
                      setDeleteOpen(true);
                    }}
                  >
                    {tr("Excluir conta e estabelecimento")}
                  </Button>
                  {lifetimeLicense ? (
                    <p className="nl-hint mt-1">{tr("Conta com licença de longa duração (vitalícia): protegida contra exclusão.")}</p>
                  ) : sub.status !== 'canceled' ? (
                    <p className="nl-hint mt-1">{tr("Cancele a assinatura acima para liberar a exclusão.")}</p>
                  ) : (
                    <p className="nl-hint mt-1">{tr("Assinatura cancelada: a conta já pode ser excluída. Um resumo fica registrado em Auditoria.")}</p>
                  )}
                </div>
              </Card>

              <Card>
                <h2 className="text-xl">{tr("Transações")}</h2>
                <p className="nl-hint mt-1">{tr("Confirmação manual é o único caminho para lançar um pagamento sem webhook assinado.")}</p>
                {detail.data?.transactions.length === 0 ? (
                  <div className="mt-4"><EmptyState title={tr("Nenhuma transação registrada")} description={tr("Nenhum teste ou cobrança foi gerada para esta empresa.")} /></div>
                ) : null}
                <div className="mt-4 grid gap-2">
                  {(detail.data?.transactions ?? []).map((tx) => (
                    <div key={tx.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line px-3 py-2">
                      <div className="min-w-0">
                        <p className="font-semibold">
                          {KIND_LABEL[tx.kind] ?? tx.kind} · {brl(tx.amount_cents)}
                        </p>
                        <p className="nl-hint truncate">
                          {tx.provider || 'provedor não informado'} · {tx.created_at ? dateTimePt(tx.created_at) : '—'}
                          {tx.provider_payment_id ? ` · id externo ${tx.provider_payment_id}` : ''}
                          {tx.paid_at ? ` · paga em ${dateTimePt(tx.paid_at)}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusPill tone={transactionTone(tx.status)}>{TX_LABEL[tx.status] ?? tx.status}</StatusPill>
                        {tx.status !== 'paid' ? (
                          <Button
                            variant="secondary"
                            disabled={busy}
                            onClick={() => {
                              setActionError(null);
                              setTxNote('');
                              setTxProviderId('');
                              setTxPending(tx);
                            }}
                          >
                            {tr("Confirmar manualmente")}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card>
                <h2 className="text-xl">{tr("Eventos da assinatura")}</h2>
                {(detail.data?.events.length ?? 0) === 0 ? (
                  <p className="nl-hint mt-2">{tr("Nenhum evento registrado até agora.")}</p>
                ) : (
                  <ul className="mt-3 grid gap-2">
                    {(detail.data?.events ?? []).map((event, index) => (
                      <li key={`${event.created_at}-${index}`} className="rounded-md bg-surface px-3 py-2">
                        <p className="font-semibold">{event.event}</p>
                        <p className="nl-hint">{dateTimePt(event.created_at)}{event.data ? ` · ${parseEvent(event.data)}` : ''}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          ) : null}
        </div>
      </div>

      {/* Modal: mudança de situação */}
      <Modal
        open={pendingStatus !== null}
        onClose={() => (busy ? undefined : setPendingStatus(null))}
        title={tr("Confirmar mudança de situação da assinatura")}
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setPendingStatus(null)}>{tr("Voltar")}</Button>
            <Button loading={busy} disabled={busy} onClick={() => pendingStatus && void applyStatus(pendingStatus)}>
              {tr("Confirmar mudança")}
            </Button>
          </>
        }
      >
        {pendingStatus ? (
          <div className="space-y-3">
            <Alert tone="warning" title={tr("Consequência imediata")}>
              {tr("A assinatura de")} <strong>{selectedBusiness?.name ?? selectedId}</strong> {tr("passa de")}{' '}
              <strong>{sub ? subscriptionLabel(sub.status) : '—'}</strong> {tr("para")}{' '}
              <strong>{subscriptionLabel(pendingStatus)}</strong>.{' '}
              {pendingStatus === 'active'
                ? 'Isso libera todo o acesso operacional da agenda sem cobrar, contornando o provedor de pagamento.'
                : pendingStatus === 'suspended' || pendingStatus === 'canceled' || pendingStatus === 'expired'
                  ? 'O acesso operacional do estabelecimento é bloqueado imediatamente e as rotas de gestão passam a exigir assinatura.'
                  : pendingStatus === 'past_due'
                    ? 'O estabelecimento entra em cobrança pendente: continua logado, mas rotas operacionais podem ser bloqueadas pelo backend.'
                    : 'O estabelecimento volta ao estado de teste gratuito.'}{' '}
              {tr("A ação é registrada em Auditoria (")}<code>{tr("admin.subscription_status_changed")}</code>{tr(") e no histórico da empresa.")}
            </Alert>
          </div>
        ) : null}
      </Modal>

      {/* Modal: cancelamento */}
      <Modal
        open={cancelOpen}
        onClose={() => (busy ? undefined : setCancelOpen(false))}
        title={tr("Cancelar assinatura do estabelecimento")}
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setCancelOpen(false)}>{tr("Voltar")}</Button>
            <Button variant="danger" loading={busy} disabled={busy} onClick={() => void applyCancel()}>
              {tr("Confirmar cancelamento")}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Alert tone="danger" title={tr("Consequência")}>
            {tr("A assinatura de")} <strong>{selectedBusiness?.name ?? selectedId}</strong> {tr("será cancelada.")}{' '}
            {cancelImmediate
              ? 'Com efeito imediato: o acesso operacional é cortado agora, sem esperar o fim do período pago.'
              : 'No fim do período atual: o estabelecimento continua operando até a data de renovação e depois perde o acesso.'}{' '}
            {tr("Registros de cobrança e histórico são preservados e a ação entra em Auditoria (")}<code>{tr("admin.subscription_cancelled")}</code>).
          </Alert>
          <label className="flex items-start gap-3 rounded-md border border-line px-3 py-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={cancelImmediate}
              onChange={(e) => setCancelImmediate(e.target.checked)}
            />
            <span>
              <span className="font-semibold">{tr("Cancelar imediatamente")}</span>
              <span className="nl-hint block">{tr("Desmarque para deixar a assinatura terminar no fim do período pago.")}</span>
            </span>
          </label>
          <div>
            <label className="nl-label" htmlFor="cancel-reason">{tr("Motivo (opcional, até 300 caracteres)")}</label>
            <Textarea
              id="cancel-reason"
              rows={3}
              maxLength={300}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={tr("Ex.: pedido do próprio dono por e-mail em 12/03.")}
            />
          </div>
        </div>
      </Modal>

      {/* Modal: exclusão da conta */}
      <Modal
        open={deleteOpen}
        onClose={() => (busy ? undefined : setDeleteOpen(false))}
        title={tr("Excluir conta e estabelecimento")}
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setDeleteOpen(false)}>{tr("Voltar")}</Button>
            <Button
              variant="danger"
              loading={busy}
              disabled={busy || !selectedBusiness || lifetimeLicense || deleteConfirm.trim() !== selectedBusiness.name}
              onClick={() => void deleteAccount()}
            >
              {tr("Excluir definitivamente")}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Alert tone="danger" title={tr("Ação definitiva e sem desfazer")}>
            {tr("Você está excluindo a conta")} <strong>{selectedBusiness?.ownerEmail ?? '—'}</strong> {tr("e o estabelecimento")}{' '}
            <strong>{selectedBusiness?.name ?? '—'}</strong>{tr(". O login deixa de existir, o perfil público sai do ar e agenda, clientes, serviços, portfólio, assinatura e transações são apagados do banco. Não há restauração pela interface.")}
          </Alert>
          <p className="text-sm">
            {tr("Um resumo (e-mail, nome, plano, total pago e data) permanece em")} <strong>{tr("Auditoria")}</strong>{tr(". Se a assinatura tiver recorrência no Asaas, ela é cancelada antes da exclusão; se o cancelamento falhar, nada é apagado.")}
          </p>
          <div>
            <label className="nl-label" htmlFor="delete-account-confirm">
              {tr("Digite")} <strong>{selectedBusiness?.name ?? 'o nome do estabelecimento'}</strong> {tr("para confirmar")}
            </label>
            <Input
              id="delete-account-confirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              maxLength={120}
            />
          </div>
          {actionError ? <Alert tone="danger" title={tr("Ação não concluída")}>{actionError}</Alert> : null}
        </div>
      </Modal>

      {/* Modal: confirmação manual de transação */}
      <Modal
        open={txPending !== null}
        onClose={() => (busy ? undefined : setTxPending(null))}
        title={tr("Confirmar pagamento manualmente")}
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setTxPending(null)}>{tr("Voltar")}</Button>
            <Button loading={busy} disabled={busy} onClick={() => void confirmManualTransaction()}>
              {tr("Confirmar recebimento")}
            </Button>
          </>
        }
      >
        {txPending ? (
          <div className="space-y-4">
            <Alert tone="danger" title={tr("Use apenas para conciliação")}>
              {tr("Você está lançando como")} <strong>{tr("pago")}</strong> {tr("uma transação de")} {brl(txPending.amount_cents)} {tr("sem webhook assinado do provedor. Isso libera o acesso operacional do estabelecimento como se o dinheiro tivesse entrado. A operação é registrada em Auditoria (")}<code>{tr("admin.payment_manually_confirmed")}</code>{tr(") com a sua identificação e a justificativa.")}
            </Alert>
            <div>
              <label className="nl-label" htmlFor="tx-note">{tr("Justificativa da conciliação")}</label>
              <Textarea
                id="tx-note"
                rows={3}
                maxLength={300}
                required
                value={txNote}
                onChange={(e) => setTxNote(e.target.value)}
                placeholder={tr("Ex.: comprovante Pix conferido no painel do provedor em 04/10, valor idêntico.")}
              />
              <p className="nl-hint mt-1">{tr("Obrigatório (3 a 300 caracteres).")}</p>
            </div>
            <div>
              <label className="nl-label" htmlFor="tx-provider-id">{tr("ID do pagamento no provedor (opcional)")}</label>
              <Input
                id="tx-provider-id"
                value={txProviderId}
                maxLength={120}
                onChange={(e) => setTxProviderId(e.target.value)}
                placeholder={tr("Ex.: 81234567890")}
              />
              <p className="nl-hint mt-1">{tr("Sem este dado, o backend grava um identificador")} <code>{tr("manual-…")}</code>.</p>
            </div>
          </div>
        ) : null}
      </Modal>
    </AdminScreen>
  );
}
